import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { Icon } from "@/components/Icon/Icon";

import { useMarkAsReadMutation } from "@/pages/Private/redux/notification/notification.api";
import { notificationSelector } from "@/pages/Private/redux/notification/notification.slice";

import { useNotificationSocket } from "../providers/NotificationSocketContext";
import { NotificationPriority, PRIORITY_COLORS } from "../enum/notification.enum";
import { Notification } from "../schema/notification";

const DISMISS_MS = 5000;
const URGENT_DISMISS_MS = 12000;

export const NotificationToast: FunctionComponent = () => {
	const { t } = useTranslation();
	const isActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);
	const { isPanelOpen } = useAppSelector(notificationSelector);
	const { latestNotification } = useNotificationSocket();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [toast, setToast] = useState<Notification | null>(null);
	const [visible, setVisible] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [markAsRead] = useMarkAsReadMutation();

	// Show toast when new notification arrives and panel is closed
	useEffect(() => {
		if (!isActive || !latestNotification || isPanelOpen) {
			return;
		}

		// Clear existing timer
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		setToast(latestNotification);
		setVisible(true);

		const delay =
			latestNotification.priority === NotificationPriority.URGENT ? URGENT_DISMISS_MS : DISMISS_MS;

		timerRef.current = setTimeout(() => {
			setVisible(false);
			// Allow exit animation before removing
			setTimeout(() => setToast(null), 300);
		}, delay);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [latestNotification, isPanelOpen, isActive]);

	const handleDismiss = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		setVisible(false);
		setTimeout(() => setToast(null), 300);
	}, []);

	const handleClick = useCallback(async () => {
		if (!toast) {
			return;
		}

		if (!toast.isRead) {
			try {
				await markAsRead(toast.id);
			} catch (e) {
				console.error("Failed to mark notification as read:", e);
			}
		}

		handleDismiss();

		if (toast.link) {
			// Normalise to absolute path (backend may omit leading "/")
			let link = toast.link;

			if (!link.startsWith("/")) {
				link = `/${link}`;
			}

			navigate(link);
		}
	}, [toast, markAsRead, navigate, handleDismiss]);

	if (!isActive || !toast) {
		return null;
	}

	const priorityColor = PRIORITY_COLORS[toast.priority] || "bg-gray-400";

	return ReactDOM.createPortal(
		<div
			className={classNames(
				"fixed top-4 right-4 w-[360px] max-w-[90vw] bg-white rounded-xl shadow-xl border border-border-light z-[10000] transition-all duration-300 ease-in-out cursor-pointer",
				visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
			)}
			onClick={handleClick}
		>
			<div className="flex flex-row items-start gap-3 p-4">
				{/* Priority dot */}
				<div className="flex-shrink-0 pt-1">
					<div className={classNames("w-2.5 h-2.5 rounded-full", priorityColor)} />
				</div>

				{/* Content */}
				<div className="flex-1 min-w-0">
					<p className="text-sm font-semibold text-gray-900 truncate">
						{toast.title || t("notifications.toast.fallbackTitle")}
					</p>
					{toast.message && (
						<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{toast.message}</p>
					)}
				</div>

				{/* Close button */}
				<button
					className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-600 cursor-pointer"
					onClick={(e) => {
						e.stopPropagation();
						handleDismiss();
					}}
				>
					<Icon icon={faXmark} size="sm" />
				</button>
			</div>
		</div>,
		document.body
	);
};
